import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { Download, Upload, FileText, RefreshCw, CheckCircle, AlertCircle, Loader2, XCircle, ChevronDown } from 'lucide-react';
import { ReportProgressBar } from './ReportProgressBar';
import { ReportSectionCard } from './ReportSectionCard';
import { REPORT_SECTIONS, ReportSection, ReportSectionId } from './types';
import {
  useReportState,
  useAssessReadiness,
  useGenerateSection,
  useSaveSection,
  useExportReport,
  useReportTemplate,
  useUploadTemplate,
  useCancelOperation,
} from '../../hooks/useReportBuilder';

interface ReportBuilderTabProps {
  projectId: string;
  sessionId?: string;
  scope: 'session' | 'project';
  token: string;
  onPinToSecondScreen?: (sectionId: string, title: string, content: string) => void;
}

export const ReportBuilderTab: React.FC<ReportBuilderTabProps> = ({
  projectId,
  sessionId,
  scope,
  token,
  onPinToSecondScreen,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const [prevExportUrl, setPrevExportUrl] = useState<string | null>(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  const { data: reportData, isLoading: stateLoading } = useReportState(projectId, sessionId, scope);
  const { data: template } = useReportTemplate(projectId);
  const assessMutation = useAssessReadiness(projectId, sessionId, scope);
  const generateMutation = useGenerateSection(projectId, sessionId, scope);
  const saveMutation = useSaveSection(projectId, sessionId, scope);
  const exportMutation = useExportReport(projectId, sessionId, scope);
  const uploadTemplateMutation = useUploadTemplate(projectId);
  const cancelMutation = useCancelOperation(projectId, sessionId, scope);

  const status = reportData?.status;
  const savedSections = reportData?.sections;

  const readinessRunning = status?.readiness === 'running' || assessMutation.isPending;
  const exportRunning = status?.exportStatus === 'running' || exportMutation.isPending;

  // Count active generation jobs
  const generatingCount = useMemo(() => {
    if (!status?.generatingSections) return 0;
    return Object.values(status.generatingSections).filter((s: any) => s.status === 'running').length;
  }, [status?.generatingSections]);

  // Auto-download when export completes
  useEffect(() => {
    if (status?.exportStatus === 'complete' && status?.exportDownloadUrl && status.exportDownloadUrl !== prevExportUrl) {
      setPrevExportUrl(status.exportDownloadUrl);
      const link = document.createElement('a');
      link.href = status.exportDownloadUrl;
      link.download = `report-${projectId}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [status?.exportStatus, status?.exportDownloadUrl, prevExportUrl, projectId]);

  // Merge saved state + readiness into full section list
  const sections: ReportSection[] = useMemo(() => {
    return REPORT_SECTIONS.map((def) => {
      const saved = savedSections?.find(s => s.id === def.id);
      const genStatus = status?.generatingSections?.[def.id];
      const isGenerating = genStatus?.status === 'running';
      return {
        id: def.id,
        title: def.title,
        readiness: saved?.readiness ?? null,
        content: saved?.content ?? null,
        generatedAt: saved?.generatedAt ?? null,
        editedAt: saved?.editedAt ?? null,
        isGenerating,
      };
    });
  }, [savedSections, status]);

  const handleAssess = useCallback(() => {
    assessMutation.mutate();
  }, [assessMutation]);

  const handleGenerate = useCallback((sectionId: ReportSectionId) => {
    generateMutation.mutate(sectionId);
  }, [generateMutation]);

  const handleSave = useCallback((sectionId: ReportSectionId, content: string) => {
    saveMutation.mutate({ sectionId, content });
  }, [saveMutation]);

  const handleSectionClick = useCallback((sectionId: string) => {
    const el = document.getElementById(`report-section-${sectionId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleExportDocx = useCallback(() => {
    setExportMenuOpen(false);
    exportMutation.mutate(undefined);
  }, [exportMutation]);

  const triggerDownload = useCallback((content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleExportJson = useCallback(() => {
    setExportMenuOpen(false);
    const payload = {
      projectId,
      sessionId: sessionId ?? null,
      scope,
      exportedAt: new Date().toISOString(),
      sections: sections.map(s => ({
        id: s.id,
        title: s.title,
        content: s.content,
        readiness: s.readiness,
        generatedAt: s.generatedAt,
        editedAt: s.editedAt,
      })),
    };
    triggerDownload(JSON.stringify(payload, null, 2), `report-${projectId}.json`, 'application/json');
  }, [projectId, sessionId, scope, sections, triggerDownload]);

  const handleExportMarkdown = useCallback(() => {
    setExportMenuOpen(false);
    const body = sections
      .map(s => `## ${s.title}\n\n${s.content ?? '_(no content)_'}`)
      .join('\n\n');
    const md = `# Report\n\n${body}\n`;
    triggerDownload(md, `report-${projectId}.md`, 'text/markdown');
  }, [projectId, sections, triggerDownload]);

  useEffect(() => {
    if (!exportMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportMenuOpen]);

  const handleTemplateUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadTemplateMutation.mutate(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [uploadTemplateMutation]);

  const hasAnyContent = sections.some(s => s.content);
  const isLoading = stateLoading;

  return (
    <div className="flex flex-col h-full">
      {/* Progress bar header */}
      <div className="px-4 py-3 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-semibold text-slate-700">Section Readiness</h2>
          <button
            onClick={handleAssess}
            disabled={readinessRunning}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary transition-colors disabled:opacity-50"
            title="Assess readiness from graph data"
          >
            <RefreshCw size={12} className={readinessRunning ? 'animate-spin' : ''} />
            {readinessRunning ? 'Assessing...' : 'Assess'}
          </button>
        </div>
        <ReportProgressBar sections={sections} onSectionClick={handleSectionClick} />
        <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> 70%+</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" /> 40-69%</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400 inline-block" /> &lt;40%</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-200 inline-block" /> No data</span>
        </div>
      </div>

      {/* Status banners */}
      {readinessRunning && (
        <div className="px-4 py-2.5 bg-gradient-to-r from-primary/5 to-blue-50 border-b border-primary/10 flex items-center gap-3">
          <Loader2 size={14} className="animate-spin text-primary shrink-0" />
          <p className="text-xs text-slate-600 flex-1">
            <span className="font-medium text-primary">Assessing readiness...</span> Querying knowledge graph to evaluate data availability for each section.
          </p>
          <button
            onClick={() => cancelMutation.mutate('readiness')}
            disabled={cancelMutation.isPending}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition-colors shrink-0"
            title="Cancel readiness assessment"
          >
            <XCircle size={14} />
            Cancel
          </button>
        </div>
      )}
      {generatingCount > 0 && (
        <div className="px-4 py-2.5 bg-gradient-to-r from-primary/5 to-blue-50 border-b border-primary/10 flex items-center gap-3">
          <Loader2 size={14} className="animate-spin text-primary shrink-0" />
          <p className="text-xs text-slate-600">
            <span className="font-medium text-primary">Generating {generatingCount} section{generatingCount > 1 ? 's' : ''}...</span> AI is composing content from your session data. This may take up to a minute.
          </p>
        </div>
      )}
      {exportRunning && (
        <div className="px-4 py-2.5 bg-gradient-to-r from-primary/5 to-blue-50 border-b border-primary/10 flex items-center gap-3">
          <Loader2 size={14} className="animate-spin text-primary shrink-0" />
          <p className="text-xs text-slate-600 flex-1">
            <span className="font-medium text-primary">Building DOCX report...</span> Compiling sections and generating the Word document.
          </p>
          <button
            onClick={() => cancelMutation.mutate('export')}
            disabled={cancelMutation.isPending}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 transition-colors shrink-0"
            title="Cancel export"
          >
            <XCircle size={14} />
            Cancel
          </button>
        </div>
      )}

      {/* Section cards */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={24} className="animate-spin text-slate-300" />
          </div>
        ) : (
          sections.map((section) => (
            <ReportSectionCard
              key={section.id}
              section={section}
              onGenerate={handleGenerate}
              onSave={handleSave}
              onPinToScreen={onPinToSecondScreen}
            />
          ))
        )}
      </div>

      {/* Export footer */}
      <div className="px-4 py-3 bg-white border-t border-slate-200">
        {/* Export status messages */}
        {status?.exportStatus === 'failed' && (
          <div className="flex items-center gap-2 mb-2 text-xs text-red-600">
            <AlertCircle size={12} />
            Export failed: {status.exportError || 'Unknown error'}
          </div>
        )}
        {status?.exportStatus === 'complete' && status?.exportDownloadUrl && (
          <div className="flex items-center gap-2 mb-2 text-xs text-emerald-600">
            <CheckCircle size={12} />
            Export ready —{' '}
            <a href={status.exportDownloadUrl} download className="underline hover:no-underline">
              Download again
            </a>
          </div>
        )}

        <div className="flex items-center gap-3">
          {/* Template selector */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <FileText size={14} className="text-slate-400 shrink-0" />
            <span className="text-xs text-slate-500 truncate">
              Template: {template?.name || 'Default'}
            </span>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-slate-500 bg-slate-100 rounded hover:bg-slate-200 transition-colors shrink-0"
            >
              <Upload size={10} />
              {template ? 'Change' : 'Upload'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx"
              onChange={handleTemplateUpload}
              className="hidden"
            />
          </div>

          {/* Export button with dropdown */}
          <div className="relative" ref={exportMenuRef}>
            <button
              onClick={() => setExportMenuOpen(o => !o)}
              disabled={!hasAnyContent || exportRunning}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                hasAnyContent && !exportRunning
                  ? 'bg-primary text-white hover:bg-primary-dark'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              {exportRunning ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={16} />
                  Export
                  <ChevronDown size={14} />
                </>
              )}
            </button>
            {exportMenuOpen && !exportRunning && (
              <div className="absolute right-0 bottom-full mb-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-10">
                <button
                  onClick={handleExportJson}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
                >
                  <FileText size={14} className="text-slate-400" />
                  JSON
                </button>
                <button
                  onClick={handleExportMarkdown}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
                >
                  <FileText size={14} className="text-slate-400" />
                  Markdown
                </button>
                <button
                  onClick={handleExportDocx}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left border-t border-slate-100"
                >
                  <FileText size={14} className="text-slate-400" />
                  DOCX
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
